import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Heart, ArrowRight, BookOpen } from "lucide-react";

const MONICA_IMAGE = "https://customer-assets.emergentagent.com/job_substack-connect/artifacts/rskmbwfc_cc7c3bcb-afc6-454e-aa8b-a9edbd692b62.jpg";
const MISSOURI_HOSPICE_BADGE = "https://customer-assets.emergentagent.com/job_8c335a57-f1ab-4e57-a235-507f65edfbc5/artifacts/dfuck8i4_bb387419-a616-466d-a17d-80f99efca60d.jpg";
const NEDA_BADGE = "https://customer-assets.emergentagent.com/job_8c335a57-f1ab-4e57-a235-507f65edfbc5/artifacts/tzebl7sx_d017ed0d-b4ff-495a-86b0-8e8164ac6451.jpg";
const VIDEO_URL_2 = "https://customer-assets.emergentagent.com/job_e1e47677-df71-49c4-917c-4fa0a875df63/artifacts/7u52hax4_1469666-hd_1920_1080_30fps.mp4";

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Monica Korba, RN | End-of-Life Doula Columbia, MO | Serenity Passage</title>
        <meta
          name="description"
          content="Meet Monica Korba, RN - a compassionate end-of-life doula in Columbia, Missouri with over 22 years of nursing experience providing holistic hospice support in Mid-Missouri."
        />
        <meta
          name="keywords"
          content="Monica Korba, end-of-life doula Columbia MO, death doula Columbia Missouri, registered nurse, holistic care, Mid-Missouri, hospice support"
        />
        <link rel="canonical" href="https://www.serenitypassage.net/about" />
      </Helmet>

      {/* Hero Banner with Video */}
      <section className="relative py-24 overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={VIDEO_URL_2} type="video/mp4" />
        </video>
        {/* White Overlay */}
        <div className="absolute inset-0 bg-white/70"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-serif text-gray-800 text-center mb-4">
            Meet Monica Korba, RN
          </h1>
          <p className="text-xl text-gray-600 text-center max-w-2xl mx-auto">
            Compassionate End-of-Life Doula serving Columbia, MO & Mid-Missouri
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Image and Badges */}
            <div className="flex flex-col items-center lg:sticky lg:top-24 space-y-6">
              <img
                src={MONICA_IMAGE}
                alt="Monica Korba, RN - End-of-Life Doula in Columbia, Missouri"
                className="rounded-2xl shadow-xl max-w-md w-full"
                data-testid="about-monica-image"
              />
              
              {/* Organization Badges */}
              <div className="flex flex-wrap justify-center gap-4">
                <img
                  src={MISSOURI_HOSPICE_BADGE}
                  alt="Missouri Hospice & Palliative Care Association Member"
                  className="h-24 w-auto object-contain rounded-lg"
                  data-testid="missouri-hospice-badge"
                />
                <img
                  src={NEDA_BADGE}
                  alt="NEDA - National End-of-Life Doula Alliance Member"
                  className="h-24 w-auto object-contain rounded-lg"
                  data-testid="neda-badge"
                />
              </div>
              
              {/* Substack Link */}
              <a
                href="https://substack.com/@monicawrites"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium transition-colors"
                data-testid="about-substack-link"
              >
                <BookOpen className="w-5 h-5" />
                Read my Substack
              </a>
            </div>

            {/* Content */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-serif text-gray-800 mb-4 flex items-center gap-2">
                  <Heart className="w-6 h-6 text-rose-500" />
                  My Journey
                </h2>
                <p className="text-gray-700 leading-relaxed text-lg mb-4">
                  With more than 22 years of experience as a Registered Nurse, I've had the privilege of caring for individuals and families through every stage of life. Through this work, I've come to understand dying as not a medical failure, but as a natural and sacred part of the human experience.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  This understanding has shaped my approach to end-of-life support, where I focus on honoring each person's unique journey while providing compassionate, holistic care that addresses emotional, practical, and spiritual needs.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-serif text-gray-800 mb-4">
                  My Philosophy
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  I believe in the strengths of traditional medicine while also embracing holistic, client-centered care that respects the whole person: mind, body, and spirit.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Every individual's end-of-life journey is unique, and I am committed to walking beside you and your loved ones with compassion, presence, and unwavering support during this sacred time.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-serif text-gray-800 mb-4">
                  What I Offer
                </h2>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-rose-400 rounded-full mt-2 flex-shrink-0"></span>
                    Non-medical end-of-life support for individuals and families
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-rose-400 rounded-full mt-2 flex-shrink-0"></span>
                    Emotional, practical, and spiritual guidance
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-rose-400 rounded-full mt-2 flex-shrink-0"></span>
                    Advance care planning assistance
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-rose-400 rounded-full mt-2 flex-shrink-0"></span>
                    Legacy and life review projects
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-rose-400 rounded-full mt-2 flex-shrink-0"></span>
                    Vigil presence and funeral planning support
                  </li>
                </ul>
              </div>

              <div className="pt-6">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-8 py-4 rounded-full font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
                  data-testid="about-contact-cta"
                >
                  Schedule a Consultation
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
